/*! TM gift-cue banner
 * Display-only. Reads /cart.js subtotal (store-wide, all products) and shows
 * progress toward the £40 free-gift threshold.
 *
 * UPDATED 2026-09 (T3, launch fixes): currency-aware. The £40 threshold and
 * the remaining-amount message are converted to the shopper's active
 * currency using window.Shopify.currency.rate (GBP-based rate; EU and
 * US/CA markets both have local currencies switched on) and formatted with
 * that currency's own symbol via Intl.NumberFormat, mirroring the pattern
 * already used in tm-shipping-cue.js. If the conversion can't be trusted
 * (Shopify.currency missing/malformed on a non-GBP storefront), the cue
 * hides itself rather than show a wrong or ambiguous number — per the
 * manifest's explicit instruction: "convert the £40 threshold the same way
 * and format in local currency, or hide it outside GBP if the behaviour
 * can't be proven."
 *
 * BUGFIX (T3 EUR/USD live verification, 2026-09-19): the non-GBP branch
 * treated the subtotal argument as GBP pence and multiplied it by the
 * exchange rate a second time, on top of the threshold's own conversion —
 * the exact same double-conversion bug found and fixed in
 * tm-shipping-cue.js on the same date (see that file's header for the live
 * repro). /cart.js already returns the subtotal in the shopper's active
 * currency on this storefront (both markets have local currencies on), so
 * only the fixed GBP-pence threshold needs converting; the subtotal is used
 * as-is. GBP is unaffected (that branch never touched the rate) and stays
 * byte-identical.
 *
 * Deliberately independent of tbm-token-bundle.js:
 *   - different namespace (tm-gift-cue vs tbm-bundle) so neither can collide
 *   - different signal: cart.total_price (subtotal) vs a qualifying product count
 *   - NO discount object, NO cart/add calls, NO checkout interaction of any kind.
 *     This banner never changes what the customer pays. The £40 threshold and
 *     gift SKU are fulfilled entirely off-cart: a Shopify Flow tags the order
 *     at >= £40 subtotal so the gift gets packed. See the 5 Jul mechanism
 *     handoff for why (Basic-plan discount stacking limit; ladder must not be
 *     touched by anything new). NOTE: the Flow trigger itself still evaluates
 *     the GBP subtotal directly (unaffected by this display-only banner), so
 *     there is no risk of the gift being mis-triggered by a currency mismatch
 *     here — this file only decides what MESSAGE to show, never what happens
 *     at fulfilment.
 *
 * Fix history (5 Jul, same day):
 *   v2: stopped anchoring to tbm-token-bundle's live wrapper directly (was
 *       re-asserting "am I right after tbmWrap" every tick, which fought
 *       with tbm's own reactive placement).
 *   v3: v2 still fought, because BOTH scripts anchor to the same Shopify
 *       element (.cart-drawer__summary) and re-assert "am I immediately
 *       before summary" every tick — inserting either wrap displaces the
 *       other by one position, so each script's insert makes the other's
 *       check fail on its next tick, and they take turns swapping places
 *       forever. Confirmed live: DOM order alternated between
 *       [tbm, tm-gift, summary] and [tm-gift, tbm, summary] across ticks.
 *       Fix: place the drawer banner ONCE per drawer element (tracked via
 *       a data attribute), then leave the DOM alone. Only re-run placement
 *       if the wrap becomes disconnected (drawer torn down and rebuilt).
 *
 * T3 (2026-09): the drawer no longer relies on the v3 place-once guard to
 * avoid fighting tbm-token-bundle over a shared anchor — cart-drawer.liquid
 * now gives this banner its OWN fixed slot ([data-tm-gift-cue-slot]), and
 * tm-cue-coordinator.js decides slot VISIBILITY (this cue only shows in the
 * drawer when the cart has no MTG single tokens; the ladder banner takes
 * the second slot instead). The place-once guard is kept regardless, since
 * it's still correct and cheap insurance against any drawer rebuild.
 */
(function () {
  'use strict';

  var configEl = document.getElementById('tm-gift-cue-data');
  if (!configEl) return;
  var CONFIG;
  try {
    CONFIG = JSON.parse(configEl.textContent);
  } catch (e) {
    console.warn('[tm-gift-cue] failed to parse data', e);
    return;
  }

  var THRESHOLD_GBP_PENCE = CONFIG.threshold_pence || 4000;
  var COPY_BELOW = (CONFIG.copy && CONFIG.copy.below) || "Spend {remaining} more and we'll tuck in a free gift.";
  var COPY_QUALIFIED = (CONFIG.copy && CONFIG.copy.qualified) || "You've qualified for a free gift. We'll tuck it in with your order.";

  var instances = new Set();
  var lastSubtotal = -1; // force first render

  // ---------- Currency helpers ----------
  // Mirrors tm-shipping-cue.js exactly. window.Shopify.currency is populated
  // by Shopify's currency.js on any storefront where a market has local
  // currencies switched on (confirmed live 2026-09: EU market and US/CA
  // market both have localCurrencies:true, base currency GBP). `.active` is
  // the shopper's currently active currency code; `.rate` is the conversion
  // multiplier FROM the shop's base currency (GBP here) TO that active
  // currency.
  function isGBP() {
    return activeCurrencyCode() === 'GBP';
  }

  function activeCurrencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || CONFIG.currency_code || 'GBP';
  }

  // Returns a positive finite rate, or null if the live currency data can't
  // be trusted (missing, non-numeric, zero/negative). Callers use null to
  // decide whether to hide the cue outside GBP rather than guess.
  function currencyRateFromGbp() {
    var raw = window.Shopify && window.Shopify.currency && window.Shopify.currency.rate;
    var rate = parseFloat(raw);
    if (!rate || isNaN(rate) || rate <= 0) return null;
    return rate;
  }

  // activePence (integer, already in the shopper's active currency) ->
  // formatted string in that currency, via Intl. No rate multiplication —
  // the caller converts a fixed GBP-pence figure (the threshold) to
  // active-currency pence before calling this; a subtotal read from
  // /cart.js is already active-currency and must not be converted again.
  function formatActiveMoney(activePence) {
    var code = activeCurrencyCode();
    var amount = activePence / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      // Intl currency code lookup can throw on an unrecognised code (defensive
      // only — Shopify.currency.active should always be a valid ISO 4217 code).
      return code + ' ' + amount.toFixed(2);
    }
  }

  function moneyGBP(pence) {
    var sign = pence < 0 ? '-' : '';
    var v = Math.abs(pence);
    var whole = Math.floor(v / 100);
    var frac = (v % 100).toString().padStart(2, '0');
    return sign + '£' + whole + (frac === '00' ? '' : '.' + frac);
  }

  // Returns { hide: true } when the cue should not render at all (non-GBP
  // storefront with no trustworthy conversion rate). Otherwise returns the
  // usual { state, message, progressPct } shape.
  //
  // subtotalPence: exactly what /cart.js reports for total_price. On the
  // GBP branch this genuinely is GBP pence (GBP is both the shop's base
  // currency and the active one). On the non-GBP branch it is already the
  // shopper's active-currency pence (Shopify's Ajax Cart API returns money
  // in the active currency once a market's local currency is switched on) —
  // only the threshold needs converting to match it.
  function compute(subtotalPence) {
    if (isGBP()) {
      if (subtotalPence >= THRESHOLD_GBP_PENCE) {
        return { state: 'qualified', message: COPY_QUALIFIED, progressPct: 100 };
      }
      var remainingGbp = THRESHOLD_GBP_PENCE - subtotalPence;
      var messageGbp = COPY_BELOW.replace('{remaining}', moneyGBP(remainingGbp));
      var pctGbp = subtotalPence <= 0 ? 0 : (subtotalPence / THRESHOLD_GBP_PENCE) * 100;
      return { state: 'below', message: messageGbp, progressPct: pctGbp };
    }

    var rate = currencyRateFromGbp();
    if (!rate) {
      // Can't prove the conversion is correct — hide rather than show a
      // wrong or ambiguous number (manifest's explicit fallback instruction).
      return { hide: true };
    }

    var subtotalActive = subtotalPence;
    var threshActive = Math.round(THRESHOLD_GBP_PENCE * rate);

    if (subtotalActive >= threshActive) {
      return { state: 'qualified', message: COPY_QUALIFIED, progressPct: 100 };
    }
    var remainingActive = threshActive - subtotalActive;
    var message = COPY_BELOW.replace('{remaining}', formatActiveMoney(remainingActive));
    var pct = subtotalActive <= 0 ? 0 : (subtotalActive / threshActive) * 100;
    return { state: 'below', message: message, progressPct: pct };
  }

  function renderInstance(root, subtotalPence) {
    if (!root || !root.isConnected) return;
    var info = compute(subtotalPence);

    if (info.hide) {
      root.setAttribute('data-state', 'hidden');
      root.hidden = true;
      return;
    }
    root.hidden = false;
    root.setAttribute('data-state', info.state);

    var msg = root.querySelector('[data-tm-gift-message]');
    if (msg) msg.textContent = info.message;

    var fill = root.querySelector('[data-tm-gift-progress-fill]');
    if (fill) fill.style.width = Math.max(0, Math.min(100, info.progressPct)) + '%';
  }

  function renderAll(subtotalPence) {
    lastSubtotal = subtotalPence;
    instances.forEach(function (el) {
      renderInstance(el, subtotalPence);
    });
  }

  if (!customElements.get('tm-gift-cue')) {
    customElements.define(
      'tm-gift-cue',
      class extends HTMLElement {
        connectedCallback() {
          instances.add(this);
          renderInstance(this, Math.max(lastSubtotal, 0));
        }
        disconnectedCallback() {
          instances.delete(this);
        }
      }
    );
  }

  var fetchInFlight = null;
  function refresh() {
    if (fetchInFlight) return fetchInFlight;
    fetchInFlight = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        var subtotal = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
        renderAll(subtotal);
      })
      .catch(function () {})
      .finally(function () {
        fetchInFlight = null;
      });
    return fetchInFlight;
  }

  // Patch fetch for /cart/* endpoints (mirrors tbm-token-bundle.js pattern,
  // kept independent so either script can be removed without breaking the other)
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function () {
      var url = '';
      try {
        url = typeof arguments[0] === 'string' ? arguments[0] : arguments[0].url || '';
      } catch (e) {}
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        Promise.resolve(p).then(function () {
          setTimeout(refresh, 60);
        });
      }
      return p;
    };
  }

  if (window.XMLHttpRequest) {
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__tmgc_url = url;
      return origOpen.apply(this, arguments);
    };
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      var url = xhr.__tmgc_url || '';
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        xhr.addEventListener('loadend', function () {
          setTimeout(refresh, 60);
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  ['cart:updated', 'cart:refresh', 'cart-update', 'shopify:cart:updated', 'cart:change'].forEach(
    function (ev) {
      document.addEventListener(ev, function () {
        refresh();
      });
    }
  );

  // Re-render (without a new /cart.js fetch) when the shopper switches
  // currency via the localization/country selector, since that changes
  // window.Shopify.currency.rate without touching the cart itself. Mirrors
  // tm-shipping-cue.js.
  document.addEventListener('change', function (e) {
    var form = e.target && e.target.closest && e.target.closest('form[data-type="localization-form"], localization-form, form.localization-form');
    if (form) {
      setTimeout(function () {
        renderAll(Math.max(lastSubtotal, 0));
      }, 300);
    }
  });

  function getTemplate() {
    return document.getElementById('tm-gift-cue-template');
  }

  function injectBefore(targetEl, wrapClass) {
    if (!targetEl || !targetEl.parentElement) return null;
    if (targetEl.parentElement.querySelector('.' + wrapClass)) return null;
    var tpl = getTemplate();
    if (!tpl) return null;
    var wrap = document.createElement('div');
    wrap.className = wrapClass;
    wrap.appendChild(tpl.content.cloneNode(true));
    targetEl.parentElement.insertBefore(wrap, targetEl);
    return wrap;
  }

  function injectAfter(targetEl, wrapClass) {
    if (!targetEl || !targetEl.parentElement) return null;
    var existing = targetEl.parentElement.querySelector('.' + wrapClass);
    if (existing) return existing;
    var tpl = getTemplate();
    if (!tpl) return null;
    var wrap = document.createElement('div');
    wrap.className = wrapClass;
    wrap.appendChild(tpl.content.cloneNode(true));
    if (targetEl.nextSibling) {
      targetEl.parentElement.insertBefore(wrap, targetEl.nextSibling);
    } else {
      targetEl.parentElement.appendChild(wrap);
    }
    return wrap;
  }

  // T3 (2026-09): fixed drawer slot. Preferred anchor is the dedicated
  // [data-tm-gift-cue-slot] element added in cart-drawer.liquid, which
  // removes the need to race tbm-token-bundle for a shared "before summary"
  // position — see cart-drawer.liquid for the anchor and
  // tm-cue-coordinator.js for the visibility toggle (this cue's slot is
  // hidden in the drawer whenever the cart has MTG single tokens, since the
  // ladder banner takes that second slot instead). The place-once guard
  // below still applies (cheap insurance against a drawer rebuild) and the
  // pre-T3 fallback path is kept for any older/uncached markup.
  function placeDrawerBanner(drawer) {
    var wrap = drawer.querySelector('.tm-gift-cue-drawer-wrap');
    if (!wrap) return false;

    // If already placed and still connected in this drawer instance, leave
    // it alone — do not re-derive "correct" position from the other
    // script's current placement, that's what causes the fight.
    if (drawer.dataset.tmGiftPlaced === 'true' && wrap.isConnected && drawer.contains(wrap)) {
      return false;
    }

    // Preferred: the dedicated fixed-slot anchor added in T3.
    var anchor = drawer.querySelector('[data-tm-gift-cue-slot]');
    if (anchor) {
      anchor.appendChild(wrap);
      drawer.dataset.tmGiftPlaced = 'true';
      return true;
    }

    // Fallback (older markup without the anchor): previous behaviour.
    var content =
      drawer.querySelector('.cart-drawer__content') ||
      drawer.querySelector('.cart-drawer__inner');
    if (!content) return false;

    var summary = content.querySelector('.cart-drawer__summary');
    if (summary) {
      content.insertBefore(wrap, summary);
      drawer.dataset.tmGiftPlaced = 'true';
      return true;
    }
    var items =
      content.querySelector('.cart-drawer__items') ||
      content.querySelector('cart-items-component');
    if (items && items.parentElement) {
      var parent = items.parentElement;
      parent.insertBefore(wrap, items.nextSibling);
      drawer.dataset.tmGiftPlaced = 'true';
      return true;
    }
    return false;
  }

  function placeAllDrawerBanners() {
    var moved = false;
    document
      .querySelectorAll(
        'cart-drawer-component, cart-drawer, dialog[id*="cart-drawer"], .cart-drawer, [data-cart-drawer]'
      )
      .forEach(function (drawer) {
        if (placeDrawerBanner(drawer)) moved = true;
      });
    return moved;
  }

  // injectBefore/injectAfter both no-op once a wrap already exists anywhere
  // under the target's parent (see the `.querySelector('.' + wrapClass)`
  // existence checks), so calling injectBefore(items, ...) is safe to call
  // every tick without re-triggering churn once the wrap has been created.
  // The only per-tick repositioning is placeDrawerBanner/placeAllDrawerBanners
  // above, which now places once per drawer element and then stops.
  function injectCartPage() {
    var items = document.querySelector('.cart-page__items');
    if (items) injectBefore(items, 'tm-gift-cue-cart-wrap');
  }

  function injectCartDrawer() {
    var drawers = document.querySelectorAll(
      'cart-drawer-component, cart-drawer, dialog[id*="cart-drawer"], .cart-drawer, [data-cart-drawer]'
    );
    drawers.forEach(function (drawer) {
      var anchor = drawer.querySelector('[data-tm-gift-cue-slot]');
      if (anchor && !anchor.querySelector('.tm-gift-cue-drawer-wrap')) {
        var tpl = getTemplate();
        if (tpl) {
          var wrap = document.createElement('div');
          wrap.className = 'tm-gift-cue-drawer-wrap';
          wrap.appendChild(tpl.content.cloneNode(true));
          anchor.appendChild(wrap);
          drawer.dataset.tmGiftPlaced = 'true';
        }
        return;
      }

      var content =
        drawer.querySelector('.cart-drawer__content') ||
        drawer.querySelector('.cart-drawer__inner');
      var summary = content && content.querySelector('.cart-drawer__summary');
      var items =
        drawer.querySelector('.cart-drawer__items') ||
        drawer.querySelector('cart-items-component');

      if (summary) {
        injectBefore(summary, 'tm-gift-cue-drawer-wrap');
      } else if (items) {
        injectAfter(items, 'tm-gift-cue-drawer-wrap');
      }

      placeDrawerBanner(drawer);
    });
  }

  function isTmGiftNode(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.tagName && node.tagName.toLowerCase() === 'tm-gift-cue') return true;
    if (node.classList) {
      if (
        node.classList.contains('tm-gift-cue') ||
        node.classList.contains('tm-gift-cue-cart-wrap') ||
        node.classList.contains('tm-gift-cue-drawer-wrap')
      ) {
        return true;
      }
    }
    return node.closest && node.closest('tm-gift-cue, .tm-gift-cue-cart-wrap, .tm-gift-cue-drawer-wrap');
  }

  function mutationIsRelevant(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (!isTmGiftNode(m.target)) {
        if (m.addedNodes && m.addedNodes.length) {
          for (var j = 0; j < m.addedNodes.length; j++) {
            if (!isTmGiftNode(m.addedNodes[j])) return true;
          }
        }
        if (m.removedNodes && m.removedNodes.length) {
          for (var k = 0; k < m.removedNodes.length; k++) {
            if (!isTmGiftNode(m.removedNodes[k])) return true;
          }
        }
        if (!m.addedNodes.length && !m.removedNodes.length) return true;
      }
    }
    return false;
  }

  function tryInject() {
    injectCartPage();
    injectCartDrawer();
  }

  function ensureInjected() {
    var cartWrapBefore = document.querySelector('.tm-gift-cue-cart-wrap');
    var drawerWrapBefore = document.querySelector('.tm-gift-cue-drawer-wrap');

    tryInject();
    var movedDrawer = placeAllDrawerBanners();

    var cartWrapAfter = document.querySelector('.tm-gift-cue-cart-wrap');
    var drawerWrapAfter = document.querySelector('.tm-gift-cue-drawer-wrap');

    var reinjected =
      (!cartWrapBefore && cartWrapAfter) ||
      (!drawerWrapBefore && drawerWrapAfter) ||
      (cartWrapBefore && !cartWrapBefore.isConnected) ||
      (drawerWrapBefore && !drawerWrapBefore.isConnected) ||
      movedDrawer;

    if (reinjected) {
      renderAll(Math.max(lastSubtotal, 0));
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    ensureInjected();
    refresh();
  });

  if (document.readyState !== 'loading') {
    ensureInjected();
    refresh();
  }

  [50, 200, 500, 1200, 3000].forEach(function (ms) {
    setTimeout(ensureInjected, ms);
  });

  var observerThrottle = null;
  var observer = new MutationObserver(function (mutations) {
    if (!mutationIsRelevant(mutations)) return;
    if (observerThrottle) return;
    observerThrottle = setTimeout(function () {
      observerThrottle = null;
      ensureInjected();
    }, 120);
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
